/*
* === SettingsContext.js (Final - Syntax Fixed) ===
*/
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import api from '../services/api';

const SettingsContext = createContext(null);

export const SettingsProvider = ({ children }) => {
    const [settings, setSettings] = useState({
        logoUrl: null,
        platformName: 'BrightSparc',
        maintenance: { active: false, endTime: '', showTimer: false },
        comingSoon: false,
        allowRegistrations: true,
        maxUploadSize: 10,
        // Security Defaults
        security: {
            sessionTimeoutMins: 30,
        },
        // Communication Defaults
        announcement: {
            active: false,
            message: '',
        },
        contact: {
            supportPhone: ''
        }
    });
    const [loading, setLoading] = useState(true);

    const fetchSettings = useCallback(async () => {
        try {
            // No auth header needed for public config
            const { data } = await api.get('/public/config');
            
            let logo = null;
            if (data.system_logo) {
                // FIXED LINE: Simplified to avoid syntax/parser errors
                const apiBase = process.env.REACT_APP_API_URL || 'https://brightsparc-api-520983012715.africa-south1.run.app';
                const baseUrl = apiBase.replace('/api', '');
                logo = `${data.system_logo}`;
            }

            // Helper for boolean parsing
            const isTrue = (val) => String(val).toLowerCase() === 'true';

            setSettings({
                logoUrl: logo,
                platformName: data.platform_name || 'BrightSparc',
                maintenance: { 
                    active: isTrue(data.maintenance_mode), 
                    endTime: data.maintenance_end_time, 
                    showTimer: isTrue(data.show_maintenance_countdown) 
                },
                comingSoon: isTrue(data.coming_soon_mode),
                allowRegistrations: data.allow_registrations !== 'false',
                maxUploadSize: parseInt(data.max_upload_size_mb || '10', 10),
                
                // Map Security Settings
                security: {
                    sessionTimeoutMins: parseInt(data.session_timeout_mins || '30', 10),
                },

                // Map Communication Settings
                announcement: {
                    active: isTrue(data.system_announcement_active),
                    message: data.system_announcement_banner || '',
                },
                contact: {
                    supportPhone: data.support_phone || ''
                }
            });
        } catch (err) {
            console.error("Failed to load system settings", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    return (
        <SettingsContext.Provider value={{ ...settings, loading, refreshSettings: fetchSettings }}>
            {children}
        </SettingsContext.Provider>
    );
};

export const useSettings = () => {
    const context = useContext(SettingsContext);
    if (!context) {
        throw new Error("useSettings must be used within a SettingsProvider");
    }
    return context;
};
